'use client'
import React, { createRef, LegacyRef, useRef, useState } from 'react'
import { MoreMenuIcon } from '../../icons'
import { createPortal } from 'react-dom'
import { Button, ButtonVariants } from '../../atoms'

import useOnClickOutside from '../../../hooks/useOutsideClick'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import { documentExists } from '../../../../MyStichtingOpenHelpers/src'

export interface ContextMenuProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode
    align?: 'left' | 'right'
    toggleButton?: React.ReactElement
}

export const ContextMenu = ({ children, align = 'right', toggleButton, className }: ContextMenuProps) => {
    const contextBtnRef = useRef<HTMLInputElement>(null)
    const contextMenuRef: LegacyRef<HTMLDivElement> | null = createRef()
    const rect = contextBtnRef?.current?.getBoundingClientRect()
    const [shouldShowMenu, setShouldShowMenu] = useState(false)
    const contextMenuRoot = documentExists() ? document.getElementById('context-menu-root') : null

    const ContextMenuModal = () => {
        const { width: windowWidth } = useWindowDimensions()

        let style: {
            top?: string
            left?: string
            right?: string
            transform?: string
        } = {
            top: `${rect?.bottom}px`,
        }

        if (align === 'right') {
            style.right = `${rect?.right ? windowWidth - rect?.right : 0}px`
        } else {
            style.left = `${rect?.left}px`
        }

        if (!documentExists()) {
            return <></>
        }

        window.addEventListener('resize', () => {
            if (shouldShowMenu) {
                setShouldShowMenu(false)
            }
        })

        return createPortal(
            <div
                ref={contextMenuRef}
                className={`context-menu inline-flex flex-col p-1 ${className ?? ''}`}
                style={style}
            >
                {children}
            </div>,
            contextMenuRoot ? contextMenuRoot : document.body,
        )
    }

    const toggleContextMenu = () => {
        setShouldShowMenu((prevValue) => !prevValue)
    }

    const handleOutsideClick = () => {
        if (shouldShowMenu) {
            setShouldShowMenu(false)
        }
    }

    useOnClickOutside(contextMenuRef, handleOutsideClick)

    return (
        <>
            <div ref={contextBtnRef} className="inline-block">
                {toggleButton && React.cloneElement(toggleButton, { onClick: () => toggleContextMenu() })}
                {!toggleButton && (
                    <Button
                        data-testid="context-menu-toggle"
                        variant={ButtonVariants.QUIET}
                        icon={<MoreMenuIcon />}
                        onClick={() => toggleContextMenu()}
                    />
                )}
            </div>

            {shouldShowMenu && <ContextMenuModal />}
        </>
    )
}
